/**
 * Lightweight form abandonment tracking engine.
 *
 * Tracks three GA4 events via the existing consent-gated `trackEvent()`:
 * - `form_start`  — first field interaction (fires once per form session)
 * - `form_submit` — form submission
 * - `form_abandon` — user leaves without submitting (beforeunload / visibilitychange)
 *
 * Uses delegated event listeners on the form element for efficiency.
 * Never captures field values — only field names and counts.
 *
 * @param {HTMLFormElement} form - The form element to track
 * @param {Object} config
 * @param {string} config.formName - GA4 form_name parameter
 * @param {string[]} [config.fields] - Explicit field list (auto-discovers if omitted)
 * @param {string} [config.formPage] - Defaults to window.location.pathname
 * @returns {{ destroy: () => void }}
 */

import { trackEvent } from './analytics';

export function createFormTracker(form, config) {
  const formName = config.formName;
  const formPage = config.formPage || window.location.pathname;

  // Auto-discover trackable fields if not explicitly provided
  const fieldNames = config.fields || discoverFields(form);

  // Internal state
  let started = false;
  let submitted = false;
  let startTime = 0;
  const touchedFields = new Set();
  const completedFields = new Set();
  let lastField = '';

  /**
   * Resolve the field name from a target element.
   *
   * @param {HTMLElement} target
   * @returns {string|null}
   */
  function getFieldName(target) {
    const name = target.name || target.id || null;
    if (!name) return null;
    return fieldNames.length === 0 || fieldNames.includes(name) ? name : null;
  }

  /**
   * Calculate elapsed seconds since form_start.
   *
   * @returns {number}
   */
  function elapsed() {
    return Math.round((performance.now() - startTime) / 1000);
  }

  // --- Delegated form listeners ---

  function onFocusin(e) {
    const name = getFieldName(e.target);
    if (!name) return;

    if (!started) {
      started = true;
      startTime = performance.now();
      trackEvent('form_start', {
        form_name: formName,
        form_page: formPage,
      });
    }

    touchedFields.add(name);
    lastField = name;
  }

  function onFocusout(e) {
    const name = getFieldName(e.target);
    if (!name) return;

    if (hasValue(e.target)) {
      completedFields.add(name);
    }
  }

  function onChange(e) {
    const name = getFieldName(e.target);
    if (!name) return;

    // Checkboxes, radios, and selects often fire change without focusin
    if (!started) {
      started = true;
      startTime = performance.now();
      trackEvent('form_start', {
        form_name: formName,
        form_page: formPage,
      });
    }

    touchedFields.add(name);
    lastField = name;

    if (hasValue(e.target)) {
      completedFields.add(name);
    }
  }

  function onSubmit() {
    submitted = true;

    const total = fieldNames.length || discoverFields(form).length;

    trackEvent('form_submit', {
      form_name: formName,
      fields_completed: completedFields.size,
      total_fields: total,
      elapsed_seconds: started ? elapsed() : 0,
    });
  }

  // --- Window listeners ---

  function fireAbandon() {
    if (!started || submitted) return;

    const total = fieldNames.length || discoverFields(form).length;
    const pct =
      total > 0 ? Math.round((completedFields.size / total) * 100) : 0;

    trackEvent('form_abandon', {
      form_name: formName,
      fields_touched: touchedFields.size,
      fields_completed: completedFields.size,
      total_fields: total,
      last_field: lastField,
      elapsed_seconds: elapsed(),
      completion_pct: pct,
    });
  }

  function onBeforeUnload() {
    fireAbandon();
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      fireAbandon();
    }
  }

  function onPageShow(e) {
    if (e.persisted) {
      // bfcache restoration — reset state for a fresh session
      started = false;
      submitted = false;
      startTime = 0;
      touchedFields.clear();
      completedFields.clear();
      lastField = '';
    }
  }

  // Attach listeners
  form.addEventListener('focusin', onFocusin);
  form.addEventListener('focusout', onFocusout);
  form.addEventListener('change', onChange);
  form.addEventListener('submit', onSubmit);
  window.addEventListener('beforeunload', onBeforeUnload);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pageshow', onPageShow);

  return {
    destroy() {
      form.removeEventListener('focusin', onFocusin);
      form.removeEventListener('focusout', onFocusout);
      form.removeEventListener('change', onChange);
      form.removeEventListener('submit', onSubmit);
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);

      started = false;
      submitted = false;
      touchedFields.clear();
      completedFields.clear();
    },
  };
}

/**
 * Auto-discover trackable field names from a form element.
 *
 * @param {HTMLFormElement} form
 * @returns {string[]}
 */
function discoverFields(form) {
  const names = new Set();
  const els = form.querySelectorAll('input, select, textarea');

  for (const el of els) {
    if (el.type === 'hidden' || el.type === 'submit') continue;
    const name = el.name || el.id;
    if (name) names.add(name);
  }

  return [...names];
}

/**
 * Check whether a field has a non-empty value.
 *
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function hasValue(el) {
  if (el.type === 'checkbox' || el.type === 'radio') {
    return el.checked;
  }
  return (el.value || '').trim() !== '';
}
